/**
 * Creates an empty grid with the specified size
 */
export function createEmptyGrid(size) {
  return Array(size).fill(null).map(() =>
    Array(size).fill(null).map(() => ({ letter: '', isBlack: false }))
  )
}

/**
 * Creates a deep copy of a grid
 */
export function copyGrid(grid) {
  return grid.map(row => row.map(cell => ({ ...cell })))
}

/**
 * Calculates numbered cells and entries for a grid
 * Returns { cells, acrossEntries, downEntries }
 */
export function calculateNumberedCells(grid) {
  if (!grid || !grid.length) return { cells: {}, acrossEntries: [], downEntries: [] }

  const gridSize = grid.length
  const cells = {}
  const acrossEntries = []
  const downEntries = []
  let num = 1

  for (let row = 0; row < gridSize; row++) {
    for (let col = 0; col < gridSize; col++) {
      if (grid[row][col].isBlack) continue

      const leftBlocked = col === 0 || grid[row][col - 1].isBlack
      const topBlocked = row === 0 || grid[row - 1][col].isBlack
      const hasRight = col < gridSize - 1 && !grid[row][col + 1].isBlack
      const hasBelow = row < gridSize - 1 && !grid[row + 1][col].isBlack

      const startsAcross = leftBlocked && hasRight
      const startsDown = topBlocked && hasBelow

      if (startsAcross || startsDown) {
        cells[`${row}-${col}`] = num

        if (startsAcross) {
          let len = 0
          let c = col
          while (c < gridSize && !grid[row][c].isBlack) {
            len++
            c++
          }
          let word = ''
          for (let i = col; i < col + len; i++) {
            word += grid[row][i].letter || '_'
          }
          acrossEntries.push({ num, row, col, length: len, word })
        }

        if (startsDown) {
          let len = 0
          let r = row
          while (r < gridSize && !grid[r][col].isBlack) {
            len++
            r++
          }
          let word = ''
          for (let i = row; i < row + len; i++) {
            word += grid[i][col].letter || '_'
          }
          downEntries.push({ num, row, col, length: len, word })
        }

        num++
      }
    }
  }

  return { cells, acrossEntries, downEntries }
}

/**
 * Gets the word at a specific cell position
 */
export function getWordAtCell(grid, row, col, direction) {
  if (!grid || grid[row][col].isBlack) return null

  const gridSize = grid.length
  let startRow = row
  let startCol = col

  // Find the start of the word
  if (direction === 'across') {
    while (startCol > 0 && !grid[row][startCol - 1].isBlack) {
      startCol--
    }
  } else {
    while (startRow > 0 && !grid[startRow - 1][col].isBlack) {
      startRow--
    }
  }

  // Collect the word cells
  const cells = []
  let r = startRow
  let c = startCol

  while (r < gridSize && c < gridSize && !grid[r][c].isBlack) {
    cells.push({ row: r, col: c, letter: grid[r][c].letter })
    if (direction === 'across') c++
    else r++
  }

  return { startRow, startCol, cells }
}

/**
 * Gets the clue number for a cell and direction
 */
export function getClueNumber(numberedCells, grid, row, col, direction) {
  const word = getWordAtCell(grid, row, col, direction)
  if (!word) return null
  return numberedCells.cells[`${word.startRow}-${word.startCol}`]
}

/**
 * Checks if a grid is fully filled (no empty non-black cells)
 */
export function isGridComplete(grid) {
  for (const row of grid) {
    for (const cell of row) {
      if (!cell.isBlack && !cell.letter) return false
    }
  }
  return true
}

/**
 * Strips letters from grid (for sending to client during solve)
 */
export function stripSolution(grid) {
  return grid.map(row => row.map(cell => ({
    isBlack: cell.isBlack,
    letter: '' // Remove solution
  })))
}

/**
 * Gets the symmetric cell position (180-degree rotational symmetry)
 */
export function getSymmetricCell(row, col, gridSize) {
  return {
    row: gridSize - 1 - row,
    col: gridSize - 1 - col
  }
}

/**
 * Pre-defined grid patterns (black square coordinates)
 * Each pattern includes minSize, maxSize, and cells array
 */
export const GRID_PATTERNS = {
  'classic_15': {
    name: 'Classic 15×15',
    minSize: 15,
    maxSize: 15,
    cells: [
      [0, 4], [0, 10],
      [1, 4], [1, 10],
      [2, 4], [2, 10],
      [3, 0], [3, 1], [3, 7],
      [4, 5], [4, 6], [4, 11], [4, 12], [4, 13], [4, 14],
      [5, 3], [5, 9],
      [6, 2], [6, 8],
      [7, 7],
    ]
  },
  'open_15': {
    name: 'Open 15×15',
    minSize: 15,
    maxSize: 15,
    cells: [
      [0, 4], [0, 10],
      [1, 4], [1, 10],
      [2, 4], [2, 10],
      [3, 4], [3, 10],
      [4, 0], [4, 1], [4, 2], [4, 3],
      [5, 7],
      [6, 5], [6, 6], [6, 8], [6, 9],
      [7, 4], [7, 10],
    ]
  },
  'standard_11': {
    name: 'Standard 11×11',
    minSize: 11,
    maxSize: 11,
    cells: [
      [0, 3], [0, 7],
      [1, 3], [1, 7],
      [2, 5],
      [3, 0], [3, 1],
      [4, 4], [4, 6], [4, 9], [4, 10],
      [5, 3], [5, 7],
    ]
  },
  'mini_9': {
    name: 'Mini 9×9',
    minSize: 9,
    maxSize: 9,
    cells: [
      [0, 4],
      [1, 4],
      [2, 2], [2, 6],
      [3, 0],
      [4, 3], [4, 5],
    ]
  },
  'simple_blocks': {
    name: 'Simple Blocks',
    minSize: 9,
    maxSize: 21,
    cells: [
      [0, 4], [0, 10],
      [4, 0], [4, 7], [4, 14],
      [7, 3], [7, 11],
    ]
  },
  'diagonal': {
    name: 'Diagonal',
    minSize: 11,
    maxSize: 15,
    cells: [
      [0, 0], [1, 1], [2, 2], [3, 3],
      [0, 5], [1, 6], [2, 7],
    ]
  },
  'empty': {
    name: 'Empty Grid',
    minSize: 5,
    maxSize: 21,
    cells: []
  }
}

/**
 * Gets available patterns for a given grid size
 */
export function getAvailablePatterns(gridSize) {
  return Object.entries(GRID_PATTERNS)
    .filter(([_, pattern]) => gridSize >= pattern.minSize && gridSize <= pattern.maxSize)
    .map(([key, pattern]) => ({
      key,
      name: pattern.name
    }))
}

/**
 * Applies a pattern to a grid with 180-degree symmetry
 */
export function applyPattern(grid, patternKey) {
  const pattern = GRID_PATTERNS[patternKey]
  if (!pattern) return grid

  const gridSize = grid.length
  const newGrid = copyGrid(grid)

  // Clear all black cells first
  for (let row = 0; row < gridSize; row++) {
    for (let col = 0; col < gridSize; col++) {
      newGrid[row][col].isBlack = false
    }
  }

  // Apply pattern cells with symmetry
  pattern.cells.forEach(([row, col]) => {
    // Scale the pattern to fit the grid size if needed
    const scaledRow = pattern.minSize === pattern.maxSize ? row : Math.floor(row * gridSize / 15)
    const scaledCol = pattern.minSize === pattern.maxSize ? col : Math.floor(col * gridSize / 15)

    if (scaledRow < gridSize && scaledCol < gridSize) {
      newGrid[scaledRow][scaledCol].isBlack = true
      newGrid[scaledRow][scaledCol].letter = ''

      // Apply symmetric cell
      const sym = getSymmetricCell(scaledRow, scaledCol, gridSize)
      newGrid[sym.row][sym.col].isBlack = true
      newGrid[sym.row][sym.col].letter = ''
    }
  })

  return newGrid
}

/**
 * Clears all cells in a grid (removes letters and black squares)
 */
export function clearGrid(grid) {
  return grid.map(row => row.map(() => ({ letter: '', isBlack: false })))
}
